import React from 'react';
import { 
  CreditCardIcon, 
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { mockBillingInfo, mockDiscounts, mockCostPrediction } from '../../services/mockData';

export const Billing: React.FC = () => {
  const billingInfo = mockBillingInfo;
  const discounts = mockDiscounts;
  const costPrediction = mockCostPrediction;

  const formatCurrency = (value: number) => `$${value.toFixed(2)}`;
  const formatDate = (dateString: string) => 
    new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });

  const getInvoiceStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'overdue':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  const usagePercentage = (billingInfo.currentBalance / billingInfo.monthlyLimit) * 100;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Billing & Usage</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Manage your billing information and track usage limits</p>
        </div>
      </div>

      {/* Current Usage & Limits */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="metric-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Current Balance</h3>
            <CreditCardIcon className="h-6 w-6 text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {formatCurrency(billingInfo.currentBalance)}
          </p>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div 
              className={`h-2 rounded-full ${usagePercentage > 90 ? 'bg-red-500' : usagePercentage > 75 ? 'bg-yellow-500' : 'bg-green-500'}`}
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {usagePercentage.toFixed(1)}% of {formatCurrency(billingInfo.monthlyLimit)} limit
          </p>
        </div>

        <div className="metric-card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">This Month</h3>
          <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            {formatCurrency(costPrediction.currentMonthCost)}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Daily average: {formatCurrency(costPrediction.dailyAverage)}
          </p>
        </div>

        <div className="metric-card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Predicted End of Month</h3>
          <p className="text-3xl font-bold text-orange-600 mb-2">
            {formatCurrency(costPrediction.predictedEndOfMonthCost)}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {costPrediction.daysRemaining} days remaining
          </p>
        </div>
      </div>

      {/* Payment Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="chart-container">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Payment Information</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Payment Method</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{billingInfo.paymentMethod}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Billing Cycle</span>
              <span className="font-medium text-gray-900 dark:text-gray-100 capitalize">{billingInfo.billingCycle}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">Next Billing Date</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatDate(billingInfo.nextBillingDate)}</span>
            </div>
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <button className="btn-primary mr-3">Update Payment Method</button>
              <button className="btn-secondary">Change Billing Cycle</button>
            </div>
          </div>
        </div>

        <div className="chart-container">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Active Discounts</h3>
          <div className="space-y-3">
            {discounts.filter(d => d.isActive).map((discount) => (
              <div key={discount.id} className="border border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-green-900 dark:text-green-100">{discount.name}</h4>
                    <p className="text-sm text-green-700 dark:text-green-300 mt-1">{discount.description}</p>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                      Valid until {formatDate(discount.validTo)}
                    </p>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-800/50 text-green-800 dark:text-green-200">
                    {discount.type === 'percentage' ? `${discount.value}%` : formatCurrency(discount.value)} off
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Invoices */}
      <div className="chart-container">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Invoices</h3>
          <button className="btn-secondary text-sm">View All Invoices</button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Invoice ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {billingInfo.invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {invoice.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(invoice.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {formatCurrency(invoice.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getInvoiceStatusIcon(invoice.status)}
                      <span className={`ml-2 text-sm font-medium ${
                        invoice.status === 'paid' ? 'text-green-600 dark:text-green-400' :
                        invoice.status === 'pending' ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'
                      }`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {invoice.downloadUrl && (
                      <button className="flex items-center text-primary-600 hover:text-primary-500">
                        <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                        Download
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Usage Alerts */}
      <div className="chart-container">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Usage Alerts</h3>
        <div className="space-y-4">
          <div className="border border-yellow-200 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-3" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Usage Warning</h4>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  You've used {usagePercentage.toFixed(1)}% of your monthly limit. 
                  Consider upgrading your plan if usage continues to increase.
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Alert Settings</h4>
            <div className="space-y-2">
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-gray-300 dark:border-gray-600 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50" defaultChecked />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Alert at 75% usage</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-gray-300 dark:border-gray-600 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50" defaultChecked />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Alert at 90% usage</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-gray-300 dark:border-gray-600 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50" />
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Weekly usage reports</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};